/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'../../lib/@waca/core-client/js/core-client/ui/core/Events',
	'../impl/Dashboard',
	'../deprecated/CanvasAPI',
	'../../dashboard/layout/authoring/interaction/Controller',
	'../../dashboard/model/BoardModelFactory',
	'../../dashboard/widgets/WidgetRegistry',
	'jquery',
	'underscore'
], function(ClassFactory, Events, DashboardController, DeprecatedCanvasAPI, Controller, BoardModelFactory, WidgetRegistry, $, _) {
	/**
	*  Dashboard service that can be used to provide a dashboard runtime environment
	*  that is needed to run code that is dashboard dependant (e.g. live widget) but without a dashboard Perspective
	*  The service will require the dashboard perspective json, and use it to provide all the needed extensions and collections
	*/
	class RuntimeEnvironment {

		constructor(bootStrapFeatures) {
			this.bootStrapFeatures = bootStrapFeatures;
			this.logger = this.bootStrapFeatures['Logger'].getAPI();
			this.productVersion = this.bootStrapFeatures['DashboardSettings'].getAPI().get('productVersion');
		}

		_getCSSLinks(settings){
			const links = document.getElementsByTagName('link');
			const loaded = {};
			Array.from(links).forEach(function(item) {
				loaded[item.getAttribute('href')] = true;
				loaded[item.getAttribute('href').split('?')[0]] = true;
			});

			const cssStyles = [];
			settings.get('applicationCssStyles').forEach(function(style){
				if (!loaded[style]){
					cssStyles.push(style);
				}
			});
			return cssStyles;
		}

		/**
		 * Obtain a dashboard runtime environment
		 * A dashboard spec must be provided by one of the following:
		 * options.spec, options.assetId or cbGetDefaultSpec
		 * @public
		 * @async
		 * @callback callback
		 * @function RuntimeEnvironment#getRuntimeEnvironment
		 * @param {Object} [options] Runtime environment options object
		 * @param {Object} [options.featuresOverride] A list of collections and features to exclude from them
		 * @example
		 *	featuresOverride: {
		 *	'com.ibm.bi.dashboard.features': {
		 *			excludes: [
		 *	 			'ToolbarDock'
		 *			]
		 * 		}
		 *	}
		 * @param {Object} [options.spec] A dashboard spec to use in runtime env
		 * @param {Object} [options.assetId] The asset id of a dashboard spec in content store to be loaded in runtime env
		 * @param {callback} [cbGetDefaultSpec] Callback function that returns a default dashboard spec
		 * @returns {Promise<Object>} Runtime environment object
		 */
		getRuntimeEnvironment(options, cbGetDefaultSpec) {

			// Load all required classes and create the services.
			return Promise.all(
				[
					'dashboard-core/js/dashboard/services/ServicesHelper',
					'dashboard-core/js/extension/Extensions',
					'dashboard-core/js/dashboard/loader/BoardLoaderHelper'
				].map(path => ClassFactory.loadModule(path))
			).then(([ _ServicesHelper, _ExtensionsHelper, _BoardModelHelper ]) => {
				const eventRouter = new Events();
				const ServicesHelper = this._serviceHelperClass || _ServicesHelper;
				const ExtensionsHelper = this._extensionsHelperClass || _ExtensionsHelper;
				const BoardModelHelper = this._boardModelHelperClass || _BoardModelHelper;


				let whenDashboardReady = null;
				let canvas;

				const boardView = {
					// TODO: this needs to be refactored
					// When dashboardRuntime service is invoked from outside of dashboard (where no canvas is present)
					// we still need to make sure the consumers get a canvas API (even though empty), or that might
					// prevent them from getting access to the runtime entirely.
					getDeprecatedCanvasWhenReady: () =>  {
						return whenDashboardReady.then(() => {
							return canvas;
						});
					},
					getDeprecatedCanvas: () =>  {
						return canvas;
					},
					canAuthor: () => {
						return false;
					},
					getBoardInfo: function(){
						return {
							type: 'exploration'
						};
					},
					getContent: function() {
						return {};
					},
					setPermissions: function() {},
					setSavedInstance: function() {}
				};

				const widgetRegistry = new WidgetRegistry();
				const dashboardController = DashboardController.create({
					glass: this.bootStrapFeatures['Glass'].getAPI(),
					settings: this.bootStrapFeatures['DashboardSettings'].getAPI(),
					view: boardView,
					eventRouter: eventRouter,
					widgetRegistry: widgetRegistry,
					profiles: options && options.profiles
				});

				const internalDashboardAPI = dashboardController.getInternalAPI();
				const dashboardApi = dashboardController.getAPI();
				const servicesHelper = new ServicesHelper({
					bootStrapFeatures: this.bootStrapFeatures,
					logger: this.logger,
					dashboardAPI: dashboardApi,
					internalDashboardAPI: internalDashboardAPI
				});

				const services = servicesHelper.createServices();

				dashboardController.setServices(services.serviceRegistry);
				dashboardController.setFeatureLoader(services.featureLoader);

				let boardSpec, boardId;
				if (options && (options.spec || options.assetId)) {
					boardSpec = options.spec;
					boardId = options.assetId;
				} else if (typeof cbGetDefaultSpec === 'function') {
					boardSpec = cbGetDefaultSpec();
				} else {
					throw new Error('A board spec is required.');
				}

				const extensionsHelper = new ExtensionsHelper(dashboardApi);
				let boardModelFactory;

				const env = {
					internalDashboardAPI: internalDashboardAPI,
					dashboardApi: dashboardApi,
					services: services,
					serviceRegistry: services.serviceRegistry,
					eventRouter: eventRouter,
					cssStyles: this._getCSSLinks(this.bootStrapFeatures['DashboardSettings'].getAPI()),
					widgetRegistry: widgetRegistry,
					dashboardFeatureLoader: services.featureLoader
				};
				whenDashboardReady = widgetRegistry.initialize(dashboardApi)
					.then(() => {
						boardModelFactory = BoardModelFactory.create({
							dashboardAPI: dashboardApi,
							glassFeature: dashboardApi.getFeature('Glass'),
							productVersion: this.productVersion,
							boardSpec: boardSpec,
							extensions: extensionsHelper,
							stringResources: dashboardApi.getDashboardCoreSvc('.StringResources'),
							eventRouter: eventRouter,
							logger: this.logger,
							boardId: boardId,
							widgetRegistry: widgetRegistry
						});
					})
					.then(extensionsHelper.load.bind(extensionsHelper))
					.then(() => {
						env.boardModelExtensions = extensionsHelper.getBoardModelExtensions();
						env.layoutExtensions = extensionsHelper.getLayoutViewExtensions() || {};
						env.canvasExtensions = extensionsHelper.getCanvasExtensions() || [];
					})
					.then(() => boardModelFactory.createBoardModel())
					.then(boardModelInfo => {
						env.boardModel = boardModelInfo.boardModel;
						boardView.boardModel =  env.boardModel;
					})
					.then(() => {
						return servicesHelper.createRuntimeServices({
							boardModel: env.boardModel,
							widgetRegistry: widgetRegistry,
							extensions: extensionsHelper,
							dashboardApi: dashboardApi
						});
					})
					.then(() => {

						// Add the canvas that includes the deprecated interface to the environment
						canvas = _.extend( new DeprecatedCanvasAPI(), services.featureLoader.getFeature('Canvas'));
						env.canvas = canvas;
						env.internalCanvas = services.featureLoader.getFeature('Canvas.internal');

						// TODO: evaluate where we use this and if we need this stored on the instance or not
						this.interactionController = new Controller({
							dashboardAPI: env.dashboardApi,
							toolbarConfig:  env.dashboardApi.getAppConfig('toolbar'),
							transaction: env.dashboardApi.getFeature('Transaction'),
							canvas: canvas,
							services: env.serviceRegistry,
							glassContext: env.glassContext,
							boardModel: env.boardModel,
							layoutController: null,
							eventRouter: env.eventRouter,
							$el: $('body'),
							appSettings: {}
						});

						const boardModelHelper = new BoardModelHelper(env.dashboardApi);
						boardModelHelper.createBoardServices({
							boardModel: env.boardModel
						});
						return env;
					});
				return whenDashboardReady;
			});
		}
	}
	return RuntimeEnvironment;
});
